import { Trash2 } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";

import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import type { OtcSavedWallet } from "../../types/cryptoSwaps.types";

export interface SavedWalletsDialogProps {
  open: boolean;
  onClose: () => void;
  wallets: OtcSavedWallet[];
  canDelete: boolean;
  onDelete: (id: string) => Promise<void>;
}

export function SavedWalletsDialog({ open, onClose, wallets, canDelete, onDelete }: SavedWalletsDialogProps) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const remove = async (id: string) => {
    setDeletingId(id);
    setFailed(false);
    try {
      await onDelete(id);
      setConfirmingId(null);
    } catch {
      setFailed(true);
    } finally {
      setDeletingId(null);
    }
  };

  const close = () => {
    setConfirmingId(null);
    setFailed(false);
    onClose();
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title={t("saved_wallets_title")}
      description={t("saved_wallets_description")}
      footer={
        <ColocaButton variant="ghost" onClick={close}>
          {t("confirm_back")}
        </ColocaButton>
      }
    >
      <div className="flex flex-col gap-3">
        {failed && <ColocaAlert variant="destructive" text={t("saved_wallets_delete_failed")} />}
        {wallets.length === 0 && <p className="text-sm text-muted-foreground">{t("saved_wallets_empty")}</p>}
        {wallets.map((wallet) => (
          <div key={wallet.id} className="flex flex-col gap-2 rounded-xl border border-border p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-col gap-1">
                <span className="text-sm font-semibold text-foreground">{wallet.alias}</span>
                <span className="font-mono text-xs break-all text-foreground">{wallet.address}</span>
                <span className="text-xs text-muted-foreground">
                  {labels.network(wallet.network)} ·{" "}
                  {wallet.ownership === "OWN"
                    ? t("saved_wallet_owner_own")
                    : t("saved_wallet_owner_third_party", { holder: wallet.holder_name ?? "—" })}
                </span>
              </div>
              {canDelete && confirmingId !== wallet.id && (
                <ColocaButton
                  size="sm"
                  variant="ghost"
                  icon={Trash2}
                  aria-label={t("saved_wallets_delete")}
                  onClick={() => setConfirmingId(wallet.id)}
                />
              )}
            </div>
            {confirmingId === wallet.id && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <span className="mr-auto text-sm text-muted-foreground">
                  {t("saved_wallets_delete_confirm")}
                </span>
                <ColocaButton size="sm" variant="ghost" onClick={() => setConfirmingId(null)}>
                  {t("confirm_back")}
                </ColocaButton>
                <ColocaButton
                  size="sm"
                  variant="destructive"
                  loading={deletingId === wallet.id}
                  onClick={() => void remove(wallet.id)}
                >
                  {t("saved_wallets_delete")}
                </ColocaButton>
              </div>
            )}
          </div>
        ))}
      </div>
    </ColocaDialog>
  );
}
